import InterfaceLanguageSwitch from "./components/language/InterfaceLanguageSwitch";
import { languageDirection } from "./utils/uiLanguage";
import useScreenTracking from "./analytics/useScreenTracking";
import React, { useContext, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Route, Switch, useLocation, Redirect } from "react-router-dom";
import { NotFoundPage } from "./components/status/StatusView";
import ErrorFallBack from "./components/errorFallBack";
import ProfileList from "./components/profileList";
import ProfileSettings from "./components/profileSettings";
import Routing from "./components/routing";
import { SessionContext } from "./context/SessionContext";
import ProtectedRoute from "./secure/protectedRoute/ProtectedRoute";
import "./style/Main.scss";
import LandingPage from "./views/landingPage";
import LoginRedirect from "./views/loginRedirect/LoginRedirect";
import ProfileSelection from "./views/profileSelection";
import ChannelControl from "./components/videoAlert/ChannelControl";
import ChannelCoverage from "./components/videoAlert/ChannelCoverage";
import ChannelLocking from "./components/videoAlert/ChannelLocking";
import PictureInPicturePlayer from "./components/pictureInPicturePlayer";
import LoginPage from "./components/loginPage";
import SelectLoginPage from "./components/selectLoginPage";
import ForgotPassword from "./components/forgotPassword";
import ResetPassword from "./components/resetPassword";
import ChatbotComponent from "./components/chatbotComponent/ChatBotComponent";
import localizationStrings from "./statics/localization";

function App() {
  const [, refreshUiTranslation] = useState(0);
  useEffect(() => {
    const update = () => refreshUiTranslation(n => n + 1);
    window.addEventListener("atlas-ui-translation-ready", update);
    return () => window.removeEventListener("atlas-ui-translation-ready", update);
  }, []);
  useScreenTracking();
  const location = useLocation();
  const { pictureInPicture, setHasLoggedIn, setProfile } =
    useContext(SessionContext);
  const [uiLanguage, setUiLanguage] = useState(() => localizationStrings.getLanguage());
  const direction = languageDirection(uiLanguage);
  // const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const dataInfo = params.get("datainfo");

  if (dataInfo) {
    sessionStorage.setItem("datainfo", dataInfo);
  }
  const fullUrl = window.location.href;

  const [baseUrl, hash] = fullUrl.split("#");
  const url = new URL(baseUrl);

  if (url.searchParams.has("datainfo")) {
    url.searchParams.delete("datainfo");
    const newUrl = `${url.origin}${url.pathname}${url.search}${
      hash ? "#" + hash : ""
    }`;
    window.history.replaceState({}, "", newUrl);
  }
  useEffect(() => {
    setHasLoggedIn(JSON.parse(localStorage.getItem("hasLoggedIn")));
    try {
      let item = JSON.parse(localStorage.getItem("profile"));
      if (item) {
        if (item?.pin) item.pin = window.atob(item.pin);
        setProfile(item);
      }
    } catch (e) {
      // Handle invalid JSON in localStorage
      console.warn('Invalid profile data in localStorage:', e);
    }
  }, []);

  // Add effect to refresh profile on window focus to catch updates from PIN creation
  useEffect(() => {
    const handleFocus = () => {
      try {
        let item = JSON.parse(localStorage.getItem("profile"));
        if (item) {
          if (item?.pin) item.pin = window.atob(item.pin);
          // Only update if the profile has actually changed
          setProfile(currentProfile => {
            if (!currentProfile || currentProfile.id !== item.id || currentProfile.hasPin !== item.hasPin) {
              return item;
            }
            // If hasPin status is the same but PIN value might have changed, update anyway
            if (currentProfile.hasPin && item.hasPin && currentProfile.pin !== item.pin) {
              return item;
            }
            return currentProfile;
          });
        }
      } catch (e) {
        console.warn('Invalid profile data in localStorage on focus:', e);
      }
    };

    // Also listen for storage events to catch profile updates
    const handleStorageChange = (e) => {
      if (e.key === 'profile' && e.newValue) {
        try {
          let item = JSON.parse(e.newValue);
          if (item) {
            if (item?.pin) item.pin = window.atob(item.pin);
            setProfile(item);
          }
        } catch (err) {
          console.warn('Invalid profile data in storage event:', err);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [setProfile]);

  useEffect(() => {
    const applyDirection = () => setUiLanguage(localizationStrings.getLanguage());
    localizationStrings.setLanguage(localStorage.getItem("language") || "eng");
    applyDirection();
    window.addEventListener("atlas-language-changed", applyDirection);
    return () => window.removeEventListener("atlas-language-changed", applyDirection);
  }, []);

  return (
    <div className="atlas-app" dir={direction}>
      {location.pathname.startsWith("/login") || location.pathname.includes("forgot") ? <InterfaceLanguageSwitch /> : null}
      <ErrorBoundary FallbackComponent={ErrorFallBack} resetKeys={[location.pathname]}>
        <div>
          <Switch>
            <Route path="/" exact>
              <Redirect to="/login" />
            </Route>
            <Route path="/redirect" component={LoginRedirect} />
            <Route path="/landingpage" component={LandingPage} />
            <Route path="/select-login" component={SelectLoginPage} />
            <Route path="/login" component={LoginPage} />
            <Route path="/forgot-password" component={ForgotPassword} />
            <Route path="/forgot-password-change/:token" component={ResetPassword} />
            <Route path="/dashboard" component={Routing} />
            <Route path={`/channelcontrol`} component={ChannelControl} />
            <Route path={`/channelcoverage`} component={ChannelCoverage} />
            <Route path={`/channellocking`} component={ChannelLocking} />
            <ProtectedRoute path="/profiles/:id">
              <ProfileList />
            </ProtectedRoute>
            <ProtectedRoute path="/profile/:profileCode">
              <ProfileSettings />
            </ProtectedRoute>
            <Route path="/selectprofile" component={ProfileSelection} />
            <Route component={NotFoundPage} />
          </Switch>
        </div>
        {pictureInPicture ? (
          <div
            className="pip-player"
            style={{
              display: pictureInPicture ? "none" : null,
              zIndex: "1000000"
            }}
          >
            <PictureInPicturePlayer />
          </div>
        ) : null}
        {/* {isChatbotOpen ? <ChatbotComponent /> : null} */}
      </ErrorBoundary>
    </div>
  );
}

export default App;
